import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Clock, Calendar, ChevronDown, ChevronUp } from 'lucide-react';

type Frequency = 'daily' | 'weekdays' | 'weekends' | 'weekly' | 'custom';
type WeekDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

interface SchedulePickerProps {
  value: string;
  onChange: (cronExpression: string) => void;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const WEEKDAY_NAMES: Record<WeekDay, string> = {
  0: 'Domingo',
  1: 'Segunda',
  2: 'Terça',
  3: 'Quarta',
  4: 'Quinta',
  5: 'Sexta',
  6: 'Sábado',
};

const FREQUENCY_OPTIONS: { value: Frequency; label: string; description: string }[] = [
  { value: 'daily', label: 'Todos os dias', description: 'Executa diariamente' },
  { value: 'weekdays', label: 'Dias úteis', description: 'Segunda a Sexta' },
  { value: 'weekends', label: 'Fins de semana', description: 'Sábado e Domingo' },
  { value: 'weekly', label: 'Uma vez por semana', description: 'Escolha o dia' },
  { value: 'custom', label: 'Personalizado', description: 'Dias específicos' },
];

function parseCronToState(cron: string): { frequency: Frequency; hour: number; selectedDays: WeekDay[] } {
  const parts = cron.split(' ');
  if (parts.length !== 5) {
    return { frequency: 'daily', hour: 20, selectedDays: [] };
  }

  const [, hour, , , dayOfWeek] = parts;
  const parsedHour = parseInt(hour) || 20;

  if (dayOfWeek === '*') {
    return { frequency: 'daily', hour: parsedHour, selectedDays: [] };
  }
  if (dayOfWeek === '1-5') {
    return { frequency: 'weekdays', hour: parsedHour, selectedDays: [] };
  }
  if (dayOfWeek === '0,6') {
    return { frequency: 'weekends', hour: parsedHour, selectedDays: [] };
  }

  const days = dayOfWeek.split(',').map(d => parseInt(d) as WeekDay);
  if (days.length === 1) {
    return { frequency: 'weekly', hour: parsedHour, selectedDays: days };
  }
  return { frequency: 'custom', hour: parsedHour, selectedDays: days };
}

function stateToCron(frequency: Frequency, hour: number, selectedDays: WeekDay[]): string {
  const hourStr = hour.toString();

  switch (frequency) {
    case 'daily':
      return `0 ${hourStr} * * *`;
    case 'weekdays':
      return `0 ${hourStr} * * 1-5`;
    case 'weekends':
      return `0 ${hourStr} * * 0,6`;
    case 'weekly': {
      const day = selectedDays[0] ?? 1;
      return `0 ${hourStr} * * ${day}`;
    }
    case 'custom': {
      if (selectedDays.length === 0) return `0 ${hourStr} * * *`;
      return `0 ${hourStr} * * ${selectedDays.sort().join(',')}`;
    }
    default:
      return `0 ${hourStr} * * *`;
  }
}

function getScheduleDescription(frequency: Frequency, hour: number, selectedDays: WeekDay[]): string {
  const hourFormatted = `${hour.toString().padStart(2, '0')}:00`;

  switch (frequency) {
    case 'daily':
      return `Todos os dias às ${hourFormatted}`;
    case 'weekdays':
      return `Segunda a Sexta às ${hourFormatted}`;
    case 'weekends':
      return `Sábado e Domingo às ${hourFormatted}`;
    case 'weekly': {
      const dayName = WEEKDAY_NAMES[selectedDays[0] ?? 1];
      return `Toda ${dayName} às ${hourFormatted}`;
    }
    case 'custom': {
      if (selectedDays.length === 0) return `Todos os dias às ${hourFormatted}`;
      const dayNames = selectedDays.map(d => WEEKDAY_NAMES[d]).join(', ');
      return `${dayNames} às ${hourFormatted}`;
    }
    default:
      return `Todos os dias às ${hourFormatted}`;
  }
}

export function SchedulePicker({ value, onChange }: SchedulePickerProps) {
  const initialState = parseCronToState(value);
  const [frequency, setFrequency] = useState<Frequency>(initialState.frequency);
  const [hour, setHour] = useState<number>(initialState.hour);
  const [selectedDays, setSelectedDays] = useState<WeekDay[]>(initialState.selectedDays);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    const newCron = stateToCron(frequency, hour, selectedDays);
    if (newCron !== value) {
      onChange(newCron);
    }
  }, [frequency, hour, selectedDays, value, onChange]);

  const toggleDay = (day: WeekDay) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort()
    );
  };

  const description = getScheduleDescription(frequency, hour, selectedDays);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
        <Calendar className="h-4 w-4" />
        <span className="font-medium text-foreground">{description}</span>
      </div>

      {/* Frequência */}
      <div className="space-y-2">
        <Label>Frequência</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {FREQUENCY_OPTIONS.filter(f => f.value !== 'custom').map((option) => (
            <Button
              key={option.value}
              type="button"
              variant={frequency === option.value ? 'default' : 'outline'}
              className="h-auto py-2 px-3 flex flex-col items-start text-left"
              onClick={() => {
                setFrequency(option.value);
                if (option.value === 'weekly' && selectedDays.length === 0) {
                  setSelectedDays([1]); // Segunda como padrão
                }
              }}
            >
              <span className="font-medium text-sm">{option.label}</span>
              <span className="text-xs opacity-70">{option.description}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Seleção de dia para frequência semanal */}
      {frequency === 'weekly' && (
        <div className="space-y-2">
          <Label>Dia da semana</Label>
          <Select
            value={selectedDays[0]?.toString() ?? '1'}
            onValueChange={(v) => setSelectedDays([parseInt(v) as WeekDay])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {([0, 1, 2, 3, 4, 5, 6] as WeekDay[]).map((day) => (
                <SelectItem key={day} value={day.toString()}>
                  {WEEKDAY_NAMES[day]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Seleção múltipla de dias para custom */}
      {frequency === 'custom' && (
        <div className="space-y-2">
          <Label>Dias da semana</Label>
          <div className="flex flex-wrap gap-2">
            {([0, 1, 2, 3, 4, 5, 6] as WeekDay[]).map((day) => (
              <Button
                key={day}
                type="button"
                variant={selectedDays.includes(day) ? 'default' : 'outline'}
                size="sm"
                className="w-10 h-10 p-0"
                onClick={() => toggleDay(day)}
              >
                {WEEKDAY_NAMES[day].slice(0, 3)}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Horário */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Horário
        </Label>
        <Select value={hour.toString()} onValueChange={(v) => setHour(parseInt(v))}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HOURS.map((h) => (
              <SelectItem key={h} value={h.toString()}>
                {h.toString().padStart(2, '0')}:00
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Toggle avançado */}
      <div className="pt-2 border-t">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-xs text-muted-foreground"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
          {showAdvanced ? 'Ocultar' : 'Mostrar'} expressão cron
        </Button>

        {showAdvanced && (
          <div className="mt-2 p-2 bg-muted rounded text-xs font-mono">
            {value}
          </div>
        )}
      </div>
    </div>
  );
}
